import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const PANEL_CLOSE_DELAY_MS = 420;

export default function ConversacionesMessageToastRail({ toasts = [], onOpen }) {
  const [peekPulse, setPeekPulse] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const prevCountRef = useRef(0);
  const closeTimerRef = useRef(null);
  const messageToasts = toasts.filter((t) => t.conversationId);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openPanel = useCallback(() => {
    clearCloseTimer();
    setPanelOpen(true);
  }, [clearCloseTimer]);

  const scheduleClosePanel = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setPanelOpen(false);
      closeTimerRef.current = null;
    }, PANEL_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    const count = messageToasts.length;
    if (count > prevCountRef.current) {
      setPeekPulse(true);
      const timer = setTimeout(() => setPeekPulse(false), 2200);
      prevCountRef.current = count;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = count;
    return undefined;
  }, [messageToasts.length]);

  const openOne = useCallback(
    (toast) => {
      onOpen?.(toast.conversationId);
    },
    [onOpen]
  );

  if (!messageToasts.length || typeof document === 'undefined') return null;

  const countLabel = messageToasts.length > 9 ? '9+' : String(messageToasts.length);

  return createPortal(
    <aside
      className={`crm-cw-msg-toast-rail${peekPulse ? ' is-pulsing' : ''}${panelOpen ? ' is-open' : ''}`}
      aria-label="Conversaciones sin leer"
    >
      <div
        className={`crm-cw-msg-toast-rail__zone${panelOpen ? ' is-active' : ''}`}
        onMouseEnter={openPanel}
        onMouseLeave={scheduleClosePanel}
        onFocusCapture={openPanel}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            scheduleClosePanel();
          }
        }}
      >
        <div className="crm-cw-msg-toast-rail__panel" role="list">
        {messageToasts.map((toast) => (
          <article key={toast.id} className="crm-cw-msg-toast" role="listitem">
            <button
              type="button"
              className="crm-cw-msg-toast__body"
              onClick={() => openOne(toast)}
            >
              <span className="crm-cw-msg-toast__kicker">
                Sin leer
                {Number(toast.unreadCount) > 1 ? (
                  <span className="crm-cw-msg-toast__unread-pill">{toast.unreadCount}</span>
                ) : null}
              </span>
              <strong className="crm-cw-msg-toast__title">{toast.title}</strong>
              <span className="crm-cw-msg-toast__preview">{toast.body}</span>
              <span className="crm-cw-msg-toast__action">Abrir y marcar leída →</span>
            </button>
          </article>
        ))}
        </div>

        <div className="crm-cw-msg-toast-rail__rail">
          <button
            type="button"
            className="crm-cw-msg-toast-rail__btn"
            aria-expanded={panelOpen}
            aria-label={`${messageToasts.length} conversación${messageToasts.length === 1 ? '' : 'es'} sin leer. Pasá el mouse para ver`}
            onClick={openPanel}
          >
            <span className="crm-cw-msg-toast-rail__btn-icon" aria-hidden="true">
              💬
            </span>
            <span
              className={`crm-cw-msg-toast-rail__badge${
                messageToasts.length > 9
                  ? ' crm-cw-msg-toast-rail__badge--max'
                  : messageToasts.length > 1
                    ? ' crm-cw-msg-toast-rail__badge--dbl'
                    : ''
              }`}
            >
              {countLabel}
            </span>
          </button>
        </div>
      </div>
    </aside>,
    document.body
  );
}
